/**
 * nhan-xet-panel.tsx — Cột "Nhận xét" của tài liệu đào tạo: ô soạn (khi vừa
 * bấm nút trên một khối), danh sách nhận xét theo thứ tự khối, trả lời, đã xử
 * lý, xoá. Neo lại theo trích đoạn do DocViewer tính (xem neo-nhan-xet.ts).
 */
import { useState } from 'react'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { Check, RotateCcw, Trash2, Reply, X, Loader2, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { apiError } from '@/lib/api-client'
import type { KhoiNeo, KetQuaNeo } from '@/lib/neo-nhan-xet'
import { useTaoNhanXet, useSuaNhanXet, useXoaNhanXet, type NhanXet } from '@/hooks/use-training'

export interface NhanXetPanelProps {
  docId: string
  canEdit: boolean
  me: string
  isAdmin: boolean
  comments: NhanXet[]
  /** Kết quả neo lại theo id nhận xét gốc. */
  neo: Map<string, KetQuaNeo>
  /** Khối vừa bấm nút "Nhận xét" — mở ô soạn. */
  soanTai: KhoiNeo | null
  onDongSoan: () => void
  /** Bấm một thẻ → cuộn tới khối. */
  onChonKhoi: (line: number | null) => void
  khoiNoiBat: number | null
}

export function NhanXetPanel({ docId, canEdit, me, isAdmin, comments, neo, soanTai, onDongSoan, onChonKhoi, khoiNoiBat }: NhanXetPanelProps) {
  const [loc, setLoc] = useState<'open' | 'resolved'>('open')
  const tao = useTaoNhanXet(docId)
  const sua = useSuaNhanXet(docId)
  const xoa = useXoaNhanXet(docId)
  const [noiDung, setNoiDung] = useState('')
  const [traLoiCho, setTraLoiCho] = useState<string | null>(null)
  const [noiDungTraLoi, setNoiDungTraLoi] = useState('')

  const goc = comments.filter((c) => !c.parentId)
  const traLoiCua = (id: string) => comments.filter((c) => c.parentId === id)
  const soMo = goc.filter((c) => c.status === 'open').length
  const soXuLy = goc.length - soMo
  const hien = goc
    .filter((c) => c.status === loc)
    .sort((a, b) => (neo.get(a.id)?.line ?? a.line) - (neo.get(b.id)?.line ?? b.line))

  async function gui() {
    if (!soanTai || !noiDung.trim()) return
    try {
      await tao.mutateAsync({ line: soanTai.line, quote: soanTai.quote, body: noiDung.trim() })
      setNoiDung('')
      onDongSoan()
      setLoc('open')
      toast.success('Đã gửi nhận xét')
    } catch (err) { toast.error(apiError(err)) }
  }
  async function guiTraLoi(parentId: string) {
    if (!noiDungTraLoi.trim()) return
    try {
      await tao.mutateAsync({ line: 0, quote: '', body: noiDungTraLoi.trim(), parentId })
      setNoiDungTraLoi(''); setTraLoiCho(null)
    } catch (err) { toast.error(apiError(err)) }
  }
  async function doiTrangThai(c: NhanXet, status: 'open' | 'resolved') {
    try { await sua.mutateAsync({ id: c.id, status }) }
    catch (err) { toast.error(apiError(err)) }
  }
  async function xoaNhanXet(c: NhanXet) {
    if (!window.confirm('Xoá nhận xét này' + (!c.parentId ? ' và các trả lời' : '') + '?')) return
    try { await xoa.mutateAsync(c.id) }
    catch (err) { toast.error(apiError(err)) }
  }

  return (
    <div className="flex h-full flex-col">
      {soanTai && (
        <div className="border-b bg-primary/5 p-3">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[11px] font-semibold text-primary">Nhận xét khối</span>
            <button type="button" className="rounded p-0.5 text-muted-foreground hover:bg-muted" onClick={onDongSoan} aria-label="Đóng"><X className="h-3.5 w-3.5" /></button>
          </div>
          <p className="mb-2 line-clamp-2 text-[11px] italic text-muted-foreground">“{soanTai.quote || '(khối không có chữ)'}”</p>
          <textarea
            autoFocus
            value={noiDung}
            onChange={(e) => setNoiDung(e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void gui() }}
            placeholder="Góp ý, yêu cầu sửa… (Ctrl+Enter để gửi)"
            className="min-h-[72px] w-full resize-y rounded-md border bg-background p-2 text-xs outline-none focus:ring-2 focus:ring-primary/30"
          />
          <div className="mt-2 flex justify-end gap-1.5">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onDongSoan}>Huỷ</Button>
            <Button size="sm" className="h-7 text-xs" disabled={!noiDung.trim() || tao.isPending} onClick={() => void gui()}>
              {tao.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Gửi
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-1 px-3 pt-3">
        {([['open', `Đang mở (${soMo})`], ['resolved', `Đã xử lý (${soXuLy})`]] as const).map(([k, nhan]) => (
          <button
            key={k} type="button" onClick={() => setLoc(k)}
            className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', loc === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}
          >
            {nhan}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {hien.length === 0 && (
          <div className="rounded-md border border-dashed p-4 text-center text-[11px] text-muted-foreground">
            <MessageSquare className="mx-auto mb-1 h-4 w-4 opacity-60" />
            {loc === 'open'
              ? (canEdit ? 'Rê chuột lên một đoạn trong tài liệu và bấm nút nhận xét để góp ý đúng chỗ.' : 'Chưa có nhận xét nào đang mở.')
              : 'Chưa có nhận xét nào được xử lý.'}
          </div>
        )}
        {hien.map((c) => {
          const n = neo.get(c.id)
          const dong = n?.line ?? null
          const noiBat = dong != null && dong === khoiNoiBat
          const suaDuoc = canEdit && (c.authorId === me || isAdmin)
          return (
            <div key={c.id} className={cn('rounded-md border bg-card p-2.5 text-xs shadow-sm', noiBat && 'border-primary ring-1 ring-primary/40')}>
              <button type="button" className="mb-1 block w-full text-left" onClick={() => onChonKhoi(dong)} title="Cuộn tới khối">
                <span className={cn('line-clamp-2 border-l-2 pl-2 text-[11px] italic', n?.daDoi ? 'border-warning text-warning' : 'border-amber-400 text-muted-foreground')}>
                  {n?.daDoi ? (dong == null ? 'Đoạn gốc không còn trong tài liệu — ' : 'Đoạn gốc đã thay đổi — ') : ''}“{c.quote || '(khối không có chữ)'}”
                </span>
              </button>
              <div className="flex items-baseline gap-1.5">
                <span className="font-semibold">{c.authorName}</span>
                <span className="text-[10px] text-muted-foreground">{dayjs(c.createdAt).format('DD/MM HH:mm')}</span>
              </div>
              <p className="mt-0.5 whitespace-pre-wrap">{c.body}</p>

              {traLoiCua(c.id).map((r) => (
                <div key={r.id} className="mt-2 border-l-2 pl-2">
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-semibold">{r.authorName}</span>
                    <span className="text-[10px] text-muted-foreground">{dayjs(r.createdAt).format('DD/MM HH:mm')}</span>
                    {canEdit && (r.authorId === me || isAdmin) && (
                      <button type="button" className="ml-auto text-muted-foreground hover:text-destructive" onClick={() => void xoaNhanXet(r)} aria-label="Xoá trả lời"><Trash2 className="h-3 w-3" /></button>
                    )}
                  </div>
                  <p className="whitespace-pre-wrap">{r.body}</p>
                </div>
              ))}

              {c.status === 'resolved' && (
                <p className="mt-1.5 text-[10px] text-success">
                  <Check className="mr-0.5 inline h-3 w-3" /> Đã xử lý{c.resolvedByName ? ` · ${c.resolvedByName}` : ''}{c.resolvedAt ? ` · ${dayjs(c.resolvedAt).format('DD/MM HH:mm')}` : ''}
                </p>
              )}

              {canEdit && (
                <div className="mt-2 flex flex-wrap items-center gap-1">
                  {traLoiCho === c.id ? (
                    <div className="w-full">
                      <textarea
                        autoFocus value={noiDungTraLoi} onChange={(e) => setNoiDungTraLoi(e.target.value)}
                        onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void guiTraLoi(c.id) }}
                        placeholder="Trả lời…" className="min-h-[56px] w-full resize-y rounded-md border bg-background p-2 text-xs outline-none focus:ring-2 focus:ring-primary/30"
                      />
                      <div className="mt-1 flex justify-end gap-1">
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => { setTraLoiCho(null); setNoiDungTraLoi('') }}>Huỷ</Button>
                        <Button size="sm" className="h-6 px-2 text-[11px]" disabled={!noiDungTraLoi.trim() || tao.isPending} onClick={() => void guiTraLoi(c.id)}>Gửi</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button type="button" className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground" onClick={() => setTraLoiCho(c.id)}>
                        <Reply className="h-3 w-3" /> Trả lời
                      </button>
                      {c.status === 'open' ? (
                        <button type="button" className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-success hover:bg-success/10" onClick={() => void doiTrangThai(c, 'resolved')}>
                          <Check className="h-3 w-3" /> Đã xử lý
                        </button>
                      ) : (
                        <button type="button" className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted" onClick={() => void doiTrangThai(c, 'open')}>
                          <RotateCcw className="h-3 w-3" /> Mở lại
                        </button>
                      )}
                      {suaDuoc && (
                        <button type="button" className="ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-destructive" onClick={() => void xoaNhanXet(c)}>
                          <Trash2 className="h-3 w-3" /> Xoá
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
